import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useExpenseCategories, useDeleteExpenseCategory, useUpdateExpenseCategory } from '@/hooks/useAccounting'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Pencil, Trash2, Plus, Lock } from 'lucide-react'
import type { ExpenseCategory } from '@/types/entities'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import { AlertModal } from '@/components/ui/alert-modal'
import { AddCategoryDialog } from './AddCategoryDialog'
import { EditCategoryDialog } from './EditCategoryDialog'

interface CategoriesListProps {
  restaurantId: string
}

export function CategoriesList({ restaurantId }: CategoriesListProps) {
  const { t, i18n } = useTranslation()
  const { data: categories, isLoading, refetch } = useExpenseCategories(restaurantId)
  const deleteCategoryMutation = useDeleteExpenseCategory(restaurantId)
  const updateCategoryMutation = useUpdateExpenseCategory(restaurantId)

  const [showAddDialog, setShowAddDialog] = useState(false)
  const [editingCategory, setEditingCategory] = useState<ExpenseCategory | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    title: string
    description: string
    onConfirm: () => void
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  })
  const [alertDialog, setAlertDialog] = useState<{
    open: boolean
    title: string
    description: string
  }>({
    open: false,
    title: '',
    description: '',
  })

  const handleDelete = (category: ExpenseCategory) => {
    if (category.isSystem) {
      setAlertDialog({
        open: true,
        title: t('accounting.cannotDeleteSystemCategory'),
        description: t('accounting.systemCategoryDescription'),
      })
      return
    }

    setConfirmDialog({
      open: true,
      title: t('accounting.deleteCategory'),
      description: t('accounting.deleteCategoryConfirm'),
      onConfirm: async () => {
        setDeletingId(category.id)
        try {
          await deleteCategoryMutation.mutateAsync(category.id)
          await refetch()
        } catch (error) {
          console.error('Failed to delete category:', error)
          if (error instanceof Error && error.message === 'CATEGORY_IN_USE') {
            setAlertDialog({
              open: true,
              title: t('accounting.categoryInUse'),
              description: t('accounting.categoryInUseDescription'),
            })
          } else {
            setAlertDialog({
              open: true,
              title: t('error.error'),
              description: t('error.generic'),
            })
          }
        } finally {
          setDeletingId(null)
        }
      },
    })
  }

  const handleToggleActive = async (category: ExpenseCategory) => {
    try {
      await updateCategoryMutation.mutateAsync({
        categoryId: category.id,
        nameAr: category.nameAr,
        nameFr: category.nameFr,
        icon: category.icon,
        color: category.color,
        isActive: !category.isActive,
      })
      await refetch()
    } catch (error) {
      console.error('Failed to toggle category:', error)
      setAlertDialog({
        open: true,
        title: t('error.error'),
        description: t('error.generic'),
      })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <div className="h-10 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className="w-4 h-4 me-2" />
          {t('accounting.addCategory')}
        </Button>
      </div>

      {!categories || categories.length === 0 ? (
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-muted-foreground">{t('accounting.noCategories')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {categories.map((category) => (
            <Card key={category.id} className={category.isActive ? '' : 'opacity-60'}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: category.color + '20' }}
                    >
                      <span style={{ color: category.color }}>
                        {/* Icon placeholder - would use lucide icons dynamically */}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">
                        {i18n.language === 'ar' ? category.nameAr : category.nameFr}
                      </h3>
                      {category.isSystem && (
                        <Badge variant="secondary" className="text-xs">
                          <Lock className="w-3 h-3 me-1" />
                          {t('accounting.system')}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Switch
                    checked={category.isActive}
                    onCheckedChange={() => handleToggleActive(category)}
                  />
                </div>

                <div
                  className="h-2 w-full rounded-full mb-3"
                  style={{ backgroundColor: category.color }}
                />

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setEditingCategory(category)}
                  >
                    <Pencil className="w-4 h-4 me-1" />
                    {t('common.edit')}
                  </Button>
                  {!category.isSystem && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(category)}
                      disabled={deletingId === category.id}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AddCategoryDialog
        restaurantId={restaurantId}
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onSuccess={() => refetch()}
      />

      {editingCategory && (
        <EditCategoryDialog
          restaurantId={restaurantId}
          category={editingCategory}
          open={!!editingCategory}
          onOpenChange={(open) => !open && setEditingCategory(null)}
          onSuccess={() => refetch()}
        />
      )}

      <ConfirmModal
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        variant="destructive"
      />

      <AlertModal
        open={alertDialog.open}
        onOpenChange={(open) => setAlertDialog({ ...alertDialog, open })}
        title={alertDialog.title}
        description={alertDialog.description}
      />
    </div>
  )
}
